import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { Layout } from '../components/Layout';
import { taskAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  CheckSquare,
  Plus,
  Loader2,
  Trash2,
  Sun,
  Moon,
  Calendar,
  RefreshCcw,
} from 'lucide-react';

export default function Tasks() {
  const { user, canManage } = useAuth();
  const { selectedLocationId, isAllLocations } = useLocation();
  const activeLocId = isAllLocations ? user?.restaurant_id : selectedLocationId;
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [activeTab, setActiveTab] = useState('opening');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    type: 'opening',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTasks();
  }, [user, activeLocId]);

  const fetchTasks = async () => {
    if (!activeLocId) {
      setLoading(false);
      return;
    }

    try {
      const response = await taskAPI.getAll(activeLocId);
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTask = async (task) => {
    try {
      const newStatus = task.status === 'completed' ? 'pending' : 'completed';
      await taskAPI.update(task.id, { status: newStatus });
      
      setTasks(prev => 
        prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t)
      );

      toast.success(newStatus === 'completed' ? 'Task completed!' : 'Task marked pending');
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await taskAPI.create(activeLocId, formData);
      toast.success('Task created!');
      setDialogOpen(false);
      setFormData({ title: '', type: 'opening', description: '' });
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (taskId) => {
    if (!confirm('Delete this task?')) return;

    try {
      await taskAPI.delete(taskId);
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleResetTasks = async () => {
    if (!confirm('Reset all tasks to pending? This is typically done at the start of a new day.')) return;

    try {
      await taskAPI.reset(activeLocId);
      toast.success('All tasks reset to pending');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to reset tasks');
    }
  };

  const getFilteredTasks = (type) => tasks.filter(t => t.type === type);

  const getCompletionStats = (type) => {
    const filtered = getFilteredTasks(type);
    const completed = filtered.filter(t => t.status === 'completed').length;
    return { completed, total: filtered.length };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  const TaskList = ({ type }) => {
    const typeTasks = getFilteredTasks(type);
    
    return (
      <div className="space-y-2">
        {typeTasks.length === 0 ? (
          <div className="text-center py-8">
            <CheckSquare className="mx-auto h-12 w-12 text-slate-300" />
            <p className="mt-2 text-slate-500">No {type} tasks</p>
            {canManage && (
              <Button onClick={() => { setFormData(prev => ({ ...prev, type })); setDialogOpen(true); }} className="mt-4">
                <Plus className="mr-2 h-4 w-4" />
                Add Task
              </Button>
            )}
          </div>
        ) : (
          typeTasks.map((task) => (
            <div
              key={task.id}
              className={`task-item ${task.status === 'completed' ? 'completed' : ''} group`}
              data-testid={`task-${task.id}`}
            >
              <Checkbox
                checked={task.status === 'completed'}
                onCheckedChange={() => handleToggleTask(task)}
                data-testid={`task-checkbox-${task.id}`}
              />
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${task.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                  {task.title}
                </p>
                {task.description && (
                  <p className="text-sm text-slate-500 truncate">{task.description}</p>
                )}
              </div>
              {canManage && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleDelete(task.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Tasks & Checklists
            </h1>
            <p className="text-slate-500 mt-1">
              Daily opening, closing, and general tasks
            </p>
          </div>

          <div className="flex gap-2">
            {canManage && (
              <>
                <Button variant="outline" onClick={handleResetTasks} data-testid="reset-tasks-btn">
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Reset All
                </Button>
                <Button onClick={() => setDialogOpen(true)} data-testid="add-task-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Add Task
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Task Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {['opening', 'closing', 'daily'].map((type) => {
            const stats = getCompletionStats(type);
            const Icon = type === 'opening' ? Sun : type === 'closing' ? Moon : Calendar;
            const bgColor = type === 'opening' ? 'bg-amber-100' : type === 'closing' ? 'bg-indigo-100' : 'bg-slate-100';
            const iconColor = type === 'opening' ? 'text-amber-600' : type === 'closing' ? 'text-indigo-600' : 'text-slate-600';

            return (
              <Card key={type} className="border-slate-200">
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 sm:w-10 sm:h-10 ${bgColor} rounded-lg flex items-center justify-center shrink-0`}>
                      <Icon className={iconColor} size={18} />
                    </div>
                    <div>
                      <p className="text-xl sm:text-2xl font-bold text-slate-900">
                        {stats.completed}/{stats.total}
                      </p>
                      <p className="text-sm text-slate-500 capitalize">{type}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Task Lists */}
        <Card className="border-slate-200">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <CardHeader className="pb-0">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="opening" data-testid="tab-opening">
                  <Sun className="mr-2 h-4 w-4" />
                  Opening
                </TabsTrigger>
                <TabsTrigger value="closing" data-testid="tab-closing">
                  <Moon className="mr-2 h-4 w-4" />
                  Closing
                </TabsTrigger>
                <TabsTrigger value="daily" data-testid="tab-daily">
                  <Calendar className="mr-2 h-4 w-4" />
                  Daily
                </TabsTrigger>
              </TabsList>
            </CardHeader>
            <CardContent className="pt-4">
              <TabsContent value="opening">
                <TaskList type="opening" />
              </TabsContent>
              <TabsContent value="closing">
                <TaskList type="closing" />
              </TabsContent>
              <TabsContent value="daily">
                <TaskList type="daily" />
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        {/* Add Task Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Task</DialogTitle>
              <DialogDescription>
                Create a new task for your team
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Task Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Check inventory"
                  required
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Task Type</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger data-testid="task-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="opening">Opening</SelectItem>
                    <SelectItem value="closing">Closing</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Add any additional details..."
                  data-testid="task-description-input"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={submitting}
                  data-testid="task-submit-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Task'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
